import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Smartphone, Monitor, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/lang";

const platformIcons = {
  iOS: Smartphone,
  Android: Smartphone,
  Web: Globe,
};

function AppStoreBadge({ href, store }) {
  if (!href) return null;
  const isIOS = store === "ios";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`store-badge-${store}`}
      className="group inline-flex items-center gap-3 glass-card rounded-2xl px-6 py-4 hover:-translate-y-1 hover:border-[#00F0FF]/40 transition-all duration-300"
    >
      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-[#00F0FF]/20 transition-colors">
        {isIOS ? (
          <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
            <path d="M3.18 23.49c.46.27 1.03.24 1.46-.06l17.47-10.12a1.43 1.43 0 0 0 0-2.47L4.64.72a1.38 1.38 0 0 0-1.46-.06A1.46 1.46 0 0 0 2.4 2.05v19.9c0 .56.3 1.08.78 1.35z" opacity="0.3"/>
            <path d="M13.54 12.07l-9.77 10.3a1.38 1.38 0 0 0 .87.35c.2 0 .42-.06.62-.17l13.17-7.64-4.89-2.84zM4.64.72C4.44.61 4.22.55 4 .55c-.33 0-.64.12-.87.35l9.77 10.3L17.79 8.4 4.64.72z"/>
            <path d="M21.11 10.84l-3.56-2.06-5.1 5.29 5.1 5.28 3.56-2.06a1.43 1.43 0 0 0 0-2.47V10.84z"/>
            <path d="M3.13.9L17.55 12.07 13.54 12.07 3.13.9z" opacity="0"/>
          </svg>
        )}
      </div>
      <div>
        <p className="text-[#A0A0AB] text-[10px] font-['JetBrains_Mono'] uppercase tracking-widest">
          {isIOS ? "Download on the" : "Get it on"}
        </p>
        <p className="text-white text-sm font-semibold -mt-0.5">
          {isIOS ? "App Store" : "Google Play"}
        </p>
      </div>
      <ExternalLink className="w-4 h-4 text-[#A0A0AB] group-hover:text-[#00F0FF] ml-2 transition-colors" />
    </a>
  );
}

export default function AppDetail() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLang();

  useEffect(() => {
    api.getApp(appId)
      .then(setApp)
      .catch(() => navigate("/"))
      .finally(() => setLoading(false));
  }, [appId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!app) return null;

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="app-detail-page">
      {/* Hero */}
      <div className="relative h-[50vh] sm:h-[60vh] overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0"
        >
          <img
            src={app.image_url}
            alt={app.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/60 to-[#05050A]/30" />
        </motion.div>

        {/* Back button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          data-testid="back-button"
          onClick={() => navigate("/")}
          className="absolute top-6 left-6 z-20 flex items-center gap-2 text-white/80 hover:text-[#00F0FF] transition-colors group"
        >
          <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-[#00F0FF]/20 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </div>
          <span className="text-sm font-medium hidden sm:block">{t("app_back")}</span>
        </motion.button>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 -mt-24 relative z-10 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="glass-card rounded-3xl p-8 sm:p-12">
            <p className="label-mono mb-4">{app.category}</p>
            <h1
              data-testid="app-detail-title"
              className="font-['Unbounded'] text-4xl sm:text-5xl font-black tracking-tighter text-white mb-3"
            >
              {app.name}
            </h1>
            <p className="text-[#00F0FF] text-lg font-['JetBrains_Mono'] font-medium mb-6">
              {app.tagline}
            </p>
            <p className="text-[#A0A0AB] text-base sm:text-lg leading-relaxed mb-8">
              {app.description}
            </p>

            {/* Platforms */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
                {t("app_available")}
              </h3>
              <div className="flex gap-4 flex-wrap">
                {app.platforms?.map((platform) => {
                  const Icon = platformIcons[platform] || Monitor;
                  return (
                    <div
                      key={platform}
                      data-testid={`platform-${platform.toLowerCase()}`}
                      className="flex items-center gap-3 glass-card rounded-2xl px-5 py-3"
                    >
                      <Icon className="w-5 h-5 text-[#00F0FF]" />
                      <span className="text-white text-sm font-medium">{platform}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Store Badges */}
            {(app.ios_link || app.android_link) && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
                  {t("app_download")}
                </h3>
                <div className="flex gap-4 flex-wrap">
                  <AppStoreBadge href={app.ios_link} store="ios" />
                  <AppStoreBadge href={app.android_link} store="android" />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              {app.website_url && (
                <a
                  href={app.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="app-website-link"
                  className="neon-btn flex items-center justify-center gap-2 text-base px-8 py-4"
                >
                  <ExternalLink className="w-5 h-5" /> {t("app_visit")}
                </a>
              )}
              <button
                data-testid="app-back-home"
                onClick={() => navigate("/")}
                className="neon-btn neon-btn-pink flex items-center justify-center gap-2 text-base px-8 py-4"
              >
                <ArrowLeft className="w-5 h-5" /> {t("app_back_home")}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
